import autosize from 'autosize';
import classNames from 'classnames';
import { useField } from 'formik';
import {
  HTMLProps,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { Label } from '~/components/ui/Label';
import styles from './TextArea.module.css';

const cx = classNames.bind(styles);

interface CommonProps extends Omit<HTMLProps<HTMLTextAreaElement>, 'ref'> {
  label?: string;
}

interface TextAreaProps extends CommonProps {
  error?: string | false;
  /** Skeleton has no skin */
  skeleton?: boolean;
  description?: string;
}

interface TextAreaFieldProps extends CommonProps {
  name: string;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    { label, error, className, skeleton, description, onKeyDown, ...props },
    ref
  ) => {
    const textarea = useRef<HTMLTextAreaElement>(null);

    useImperativeHandle(ref, () => textarea.current!, []);

    useLayoutEffect(() => {
      const el = textarea.current;
      if (el) {
        // console.log('initializing autosize');
        autosize(el);
        return () => {
          // console.log('destroying autosize');
          autosize.destroy(el);
        };
      }
    }, []);

    useLayoutEffect(() => {
      if (textarea.current) {
        // Update autosize when value changes programmatically so height matches content
        autosize.update(textarea.current);
      }
    }, [props.value]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Pass event to caller first in case they need to handle/stop it
      onKeyDown?.(event);
      if (event.defaultPrevented) return;

      // Prevent bubbling of destructive keys to parents (which may treat
      // Backspace/Delete as navigation/close). Keep default so text still edits.
      if (event.key === 'Backspace' || event.key === 'Delete') {
        event.stopPropagation();
      }
    };

    return (
      <div className={cx('group', className)}>
        {label && <Label className='mb-1.5'>{label}</Label>}
        <textarea
          ref={textarea}
          className={classNames([
            'w-full rounded px-3 py-2.5 outline-none',
            error
              ? 'border-red-600 focus-visible:border-red-600'
              : skeleton
              ? ''
              : 'border-gray88',
            !skeleton &&
              'input mt-1 w-full border focus-visible:border-brand focus-visible:outline-none',
          ])}
          onKeyDown={handleKeyDown}
          {...props}
        />
        {description && (
          <p className='ml-1 text-xs text-secondary'>{description}</p>
        )}
        {error && <div className='ErrorMessage'>{error}</div>}
      </div>
    );
  }
);

TextArea.displayName = 'TextArea';

export const TextAreaField = ({
  name,
  label,
  ...props
}: TextAreaFieldProps) => {
  const [field, meta] = useField(name);

  const error =
    meta.touched &&
    meta.error &&
    (typeof meta.error === 'string' ? meta.error : JSON.stringify(meta.error));

  return <TextArea label={label} error={error} {...field} {...props} />;
};
